import React from "react";
import { NavLink, useHistory, useParams } from "react-router-dom";
import { match } from "ts-pattern";

import { CTA } from "src/components/cta/cta";
import { Separator } from "src/components/separator/separator";
import { ReportEdge, School } from "src/generated/types";
import { Routes } from "src/utils/routes";
import { ReportTabsCombined } from "src/components/report_tabs_combined/report_tabs_combined";

import { ExpertiseResources } from "./sections/expertise_resources/expertise_resources";
import { Purpose } from "./sections/purpose/purpose";
import { Relationships } from "./sections/relationships/relationships";
import { TeachingAndLearning } from "./sections/teaching_and_learning/teaching_and_learning";
import { WellBeing } from "./sections/well_being/well_being";
import { Overview } from "./sections/overview/overview";
import "../../report.scss";
/*
 * Types
 */

interface ReportDetailPageProps {
    readonly reports: Array<ReportEdge>;
    readonly school: School;
}

type SectionPath =
    | "expertise-and-resources"
    | "overview"
    | "purpose"
    | "relationships"
    | "teaching-and-learning"
    | "well-being";

interface SectionUrlPath {
    readonly section: SectionPath;
}

/*
 * Main
 */

export const ReportPartialPage = (props: ReportDetailPageProps): React.ReactElement => {
    const { reports, school } = props;

    const history = useHistory();
    const { section } = useParams<SectionUrlPath>();

    const scrollAndPush = (toPush: string): void => {
        history.push(toPush);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const getLink = (section: string) => {
        return `/reports/${reports[0]?.node?.type}/${section}`;
    };

    const renderDomainLinks = (): React.ReactElement => {
        return (
            <>
                <NavLink className="step-link" exact to={getLink(Routes.section.purpose)}>
                    Purpose
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.relationships)}>
                    Relationships
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.teachingAndLearning)}>
                    Teaching &amp; Learning
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.expertiseAndResources)}>
                    Expertise &amp; Resources
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.wellBeing)}>
                    Well-Being
                </NavLink>
            </>
        );
    };

    const renderDomain = (): React.ReactElement => {
        return match(section)
            .with("expertise-and-resources", () => {
                return (
                    <>
                        <ExpertiseResources reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.wellBeing))}
                            subtitle="Next:"
                            title="Explore the Well-Being Domain"
                        />
                    </>
                );
            })
            .with("overview", () => {
                return (
                    <>
                        <Overview />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.purpose))}
                            subtitle="Next:"
                            title="Explore the Five Domains"
                        />
                    </>
                );
            })
            .with("purpose", () => {
                return (
                    <>
                        <Purpose reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.relationships))}
                            subtitle="Next:"
                            title="Explore the Relationships Domain"
                        />
                    </>
                );
            })
            .with("relationships", () => {
                return (
                    <>
                        <Relationships reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.teachingAndLearning))}
                            subtitle="Next:"
                            title="Explore the Teaching & Learning Domain"
                        />
                    </>
                );
            })
            .with("teaching-and-learning", () => {
                return (
                    <>
                        <TeachingAndLearning reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.expertiseAndResources))}
                            subtitle="Next:"
                            title="Explore the Expertise & Resources Domain"
                        />
                    </>
                );
            })
            .with("well-being", () => {
                return (
                    <>
                        <WellBeing reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.purpose))}
                            subtitle="Next:"
                            title="Explore the Purpose Domain"
                        />
                    </>
                );
            })
            .exhaustive();
    };

    return (
        <>
            <div className="report-page-top">
                <ReportTabsCombined currentReports={reports} school={school} />
                <Separator />
                <div className="report-internal-navigation">
                    <div className="internal-nav-top-line">
                        <NavLink className="step-link" exact to={getLink(Routes.section.overview)}>
                            Overview
                        </NavLink>
                        <div className="clear" />
                    </div>
                    <div className="internal-nav-bottom-line">{renderDomainLinks()}</div>
                </div>
            </div>
            {renderDomain()}
            <div className="internal-nav-bottom-line center">{renderDomainLinks()}</div>
        </>
    );
};
